import { useState, useEffect } from 'react'
import { Table, Button, Badge, Modal, Form, Row, Col, Card } from 'react-bootstrap'
import { toast } from 'react-toastify'
import { pagingApi, extensionsApi } from '../services/api'
import { useConfirm } from '../components/common/ConfirmDialog'

interface PagingGroup {
  id: string
  name: string
  number: string
  description: string
  duplex: boolean
  announcement_recording_id: string
  busy_behavior: string
  timeout: number
  is_active: boolean
  members: string[]
}

const emptyForm = {
  name: '',
  number: '',
  description: '',
  duplex: false,
  announcement_recording_id: '',
  busy_behavior: 'skip',
  timeout: 60,
  is_active: true,
  members: [] as string[]
}

const busyBehaviors = [
  { value: 'skip', label: 'Überspringen' },
  { value: 'ring', label: 'Trotzdem klingeln' },
  { value: 'force', label: 'Erzwingen' }
]

export default function Paging() {
  const confirm = useConfirm()
  const [groups, setGroups] = useState<PagingGroup[]>([])
  const [loading, setLoading] = useState(true)
  const [showModal, setShowModal] = useState(false)
  const [editingGroup, setEditingGroup] = useState<PagingGroup | null>(null)
  const [formData, setFormData] = useState(emptyForm)
  const [extensions, setExtensions] = useState<{extension: string, name: string}[]>([])

  useEffect(() => {
    loadGroups()
    loadExtensions()
  }, [])

  const loadGroups = async () => {
    try {
      const response = await pagingApi.list()
      setGroups(response.data.items || [])
    } catch {
      toast.error('Fehler beim Laden')
    } finally {
      setLoading(false)
    }
  }

  const loadExtensions = async () => {
    try {
      const response = await extensionsApi.list()
      setExtensions(response.data.items || [])
    } catch {
      console.error('Error loading extensions')
    }
  }

  const openCreateModal = () => {
    setEditingGroup(null)
    setFormData(emptyForm)
    setShowModal(true)
  }

  const openEditModal = (group: PagingGroup) => {
    setEditingGroup(group)
    setFormData({
      name: group.name,
      number: group.number,
      description: group.description || '',
      duplex: group.duplex ?? false,
      announcement_recording_id: group.announcement_recording_id || '',
      busy_behavior: group.busy_behavior || 'skip',
      timeout: group.timeout || 60,
      is_active: group.is_active,
      members: group.members || []
    })
    setShowModal(true)
  }

  const handleSubmit = async (e: React.FormEvent) => {
    e.preventDefault()
    try {
      const submitData: Record<string, unknown> = { ...formData }
      if (!submitData.announcement_recording_id) delete submitData.announcement_recording_id

      if (editingGroup) {
        await pagingApi.update(editingGroup.id, submitData)
        toast.success('Durchsage-Gruppe aktualisiert')
      } else {
        await pagingApi.create(submitData)
        toast.success('Durchsage-Gruppe erstellt')
      }
      setShowModal(false)
      loadGroups()
    } catch {
      toast.error('Fehler beim Speichern')
    }
  }

  const handleDelete = async (group: PagingGroup) => {
    const confirmed = await confirm({
      title: 'Durchsage-Gruppe löschen',
      message: `Durchsage-Gruppe "${group.name}" wirklich löschen?`,
      confirmText: 'Löschen',
      variant: 'danger'
    })
    if (!confirmed) return
    try {
      await pagingApi.delete(group.id)
      toast.success('Durchsage-Gruppe gelöscht')
      loadGroups()
    } catch {
      toast.error('Fehler beim Löschen')
    }
  }

  const toggleMember = (ext: string) => {
    const newMembers = formData.members.includes(ext)
      ? formData.members.filter(m => m !== ext)
      : [...formData.members, ext]
    setFormData({ ...formData, members: newMembers })
  }

  return (
    <div>
      <div className="d-flex justify-content-between align-items-center mb-4">
        <h4>Durchsagen (Paging)</h4>
        <Button variant="primary" onClick={openCreateModal}>
          <i className="bi bi-plus-lg me-1"></i> Neue Durchsage-Gruppe
        </Button>
      </div>

      <div className="table-container">
        <Table hover className="mb-0">
          <thead>
            <tr>
              <th>Nummer</th>
              <th>Name</th>
              <th>Mitglieder</th>
              <th>Modus</th>
              <th>Status</th>
              <th className="text-end">Aktionen</th>
            </tr>
          </thead>
          <tbody>
            {loading ? (
              <tr><td colSpan={6} className="text-center py-4">Laden...</td></tr>
            ) : groups.length === 0 ? (
              <tr><td colSpan={6} className="text-center py-4 text-muted">Keine Durchsage-Gruppen</td></tr>
            ) : (
              groups.map(g => (
                <tr key={g.id}>
                  <td><strong>{g.number}</strong></td>
                  <td>{g.name}</td>
                  <td><Badge bg="info">{(g.members || []).length}</Badge></td>
                  <td><Badge bg={g.duplex ? 'warning' : 'secondary'}>{g.duplex ? 'Duplex' : 'Simplex'}</Badge></td>
                  <td><Badge bg={g.is_active ? 'success' : 'secondary'}>{g.is_active ? 'Aktiv' : 'Inaktiv'}</Badge></td>
                  <td className="text-end">
                    <Button variant="outline-primary" size="sm" className="me-1" onClick={() => openEditModal(g)}>
                      <i className="bi bi-pencil"></i>
                    </Button>
                    <Button variant="outline-danger" size="sm" onClick={() => handleDelete(g)}>
                      <i className="bi bi-trash"></i>
                    </Button>
                  </td>
                </tr>
              ))
            )}
          </tbody>
        </Table>
      </div>

      <Modal show={showModal} onHide={() => setShowModal(false)} size="lg">
        <Modal.Header closeButton>
          <Modal.Title>{editingGroup ? 'Durchsage-Gruppe bearbeiten' : 'Neue Durchsage-Gruppe'}</Modal.Title>
        </Modal.Header>
        <Form onSubmit={handleSubmit}>
          <Modal.Body>
            <Row>
              <Col md={4}>
                <Form.Group className="mb-3">
                  <Form.Label>Nummer *</Form.Label>
                  <Form.Control
                    value={formData.number}
                    onChange={(e) => setFormData({...formData, number: e.target.value})}
                    required
                    placeholder="z.B. 80"
                  />
                </Form.Group>
              </Col>
              <Col md={4}>
                <Form.Group className="mb-3">
                  <Form.Label>Name *</Form.Label>
                  <Form.Control
                    value={formData.name}
                    onChange={(e) => setFormData({...formData, name: e.target.value})}
                    required
                    placeholder="z.B. Alle Telefone"
                  />
                </Form.Group>
              </Col>
              <Col md={4}>
                <Form.Group className="mb-3">
                  <Form.Label>Timeout (Sek.)</Form.Label>
                  <Form.Control
                    type="number"
                    value={formData.timeout}
                    onChange={(e) => setFormData({...formData, timeout: parseInt(e.target.value) || 60})}
                    min={10}
                    max={300}
                  />
                </Form.Group>
              </Col>
            </Row>

            <Form.Group className="mb-3">
              <Form.Label>Beschreibung</Form.Label>
              <Form.Control
                value={formData.description}
                onChange={(e) => setFormData({...formData, description: e.target.value})}
                placeholder="Optionale Beschreibung"
              />
            </Form.Group>

            <Card className="mb-3">
              <Card.Header>Mitglieder</Card.Header>
              <Card.Body>
                <div className="d-flex flex-wrap gap-2">
                  {extensions.map(ext => (
                    <Button
                      key={ext.extension}
                      variant={formData.members.includes(ext.extension) ? 'primary' : 'outline-secondary'}
                      size="sm"
                      onClick={() => toggleMember(ext.extension)}
                    >
                      {ext.extension} - {ext.name}
                    </Button>
                  ))}
                </div>
                {extensions.length === 0 && (
                  <p className="text-muted mb-0">Keine Nebenstellen verfügbar</p>
                )}
              </Card.Body>
            </Card>

            <Row>
              <Col md={4}>
                <Form.Group className="mb-3">
                  <Form.Label>Besetzt-Verhalten</Form.Label>
                  <Form.Select
                    value={formData.busy_behavior}
                    onChange={(e) => setFormData({...formData, busy_behavior: e.target.value})}
                  >
                    {busyBehaviors.map(b => (
                      <option key={b.value} value={b.value}>{b.label}</option>
                    ))}
                  </Form.Select>
                </Form.Group>
              </Col>
              <Col md={4}>
                <Form.Group className="mb-3 pt-4">
                  <Form.Check
                    type="switch"
                    id="duplex"
                    label="Duplex (Intercom)"
                    checked={formData.duplex}
                    onChange={(e) => setFormData({...formData, duplex: e.target.checked})}
                  />
                  <Form.Text className="text-muted">Ermöglicht Rückantwort</Form.Text>
                </Form.Group>
              </Col>
              <Col md={4}>
                <Form.Group className="mb-3 pt-4">
                  <Form.Check
                    type="switch"
                    id="is_active"
                    label="Aktiv"
                    checked={formData.is_active}
                    onChange={(e) => setFormData({...formData, is_active: e.target.checked})}
                  />
                </Form.Group>
              </Col>
            </Row>
          </Modal.Body>
          <Modal.Footer>
            <Button variant="secondary" onClick={() => setShowModal(false)}>Abbrechen</Button>
            <Button variant="primary" type="submit">
              {editingGroup ? 'Speichern' : 'Erstellen'}
            </Button>
          </Modal.Footer>
        </Form>
      </Modal>
    </div>
  )
}
